import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Activity, Search, Filter, ChevronLeft, ChevronRight, X, User } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function ActivityLogs() {
  const { t } = useTranslation('activityLogs');
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 30;

  // Filters
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');

  // Filter options from API
  const [filterOptions, setFilterOptions] = useState({ actions: [], entity_types: [], users: [] });

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/activity-logs/filters`, { withCredentials: true });
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('per_page', perPage);
      if (action) params.append('action', action);
      if (entityType) params.append('entity_type', entityType);
      if (selectedUserId) params.append('user_id', selectedUserId);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (search) params.append('search', search);

      const response = await axios.get(`${API_URL}/api/activity-logs?${params.toString()}`, { withCredentials: true });
      setLogs(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      toast.error(t('toast.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [page, action, entityType, selectedUserId, dateFrom, dateTo, search, t]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const clearFilters = () => {
    setAction('');
    setEntityType('');
    setSelectedUserId('');
    setDateFrom('');
    setDateTo('');
    setSearch('');
    setPage(1);
  };

  const hasFilters = action || entityType || selectedUserId || dateFrom || dateTo || search;

  const getActionColor = (act) => {
    switch (act) {
      case 'create': return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400';
      case 'update': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400';
      case 'delete': return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';
      case 'login': return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
      case 'logout': return 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400';
      case 'assign': return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400';
      case 'unassign': return 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400';
      case 'reset': return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400';
      case 'send_invoice': return 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400';
      case 'send_invoice_failed': return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';
      case 'send_invoice_skipped': return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400';
      default: return 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400';
    }
  };

  const getEntityBadge = (et) => {
    const colors = {
      user: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400',
      project: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400',
      income: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
      expense: 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400',
      employee: 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400',
      assignment: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
      password: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
      profile: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
    };
    return colors[et] || 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400';
  };

  // Translated labels for known actions / entity types; unknown values fall back to the raw value.
  const actionLabel = (act) => t(`actions.${act}`, { defaultValue: act.replace(/_/g, ' ') });
  const entityLabel = (et) => t(`entities.${et}`, { defaultValue: et.replace(/_/g, ' ') });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('uz-UZ', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="activity-logs-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 dark:text-slate-100 tracking-tight" style={{ fontFamily: "'Outfit', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>{t('title')}</h1>
        <p className="text-slate-600 dark:text-slate-400 mt-2" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
          {t('subtitle')} {total > 0 && <span className="text-slate-500">({t('records', { count: total })})</span>}
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="h-4 w-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('filters.title')}</span>
            {hasFilters && (
              <Button size="sm" variant="ghost" onClick={clearFilters} className="ml-auto text-xs h-7">
                <X className="h-3 w-3 mr-1" /> {t('filters.clear')}
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={t('filters.searchPlaceholder')}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-9 h-9"
              />
            </div>

            {/* Action filter */}
            <Select value={action} onValueChange={(val) => { setAction(val === 'all' ? '' : val); setPage(1); }}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder={t('filters.action')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('filters.allActions')}</SelectItem>
                {filterOptions.actions.map(a => (
                  <SelectItem key={a} value={a}>{actionLabel(a)}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Entity type filter */}
            <Select value={entityType} onValueChange={(val) => { setEntityType(val === 'all' ? '' : val); setPage(1); }}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder={t('filters.entityType')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('filters.allTypes')}</SelectItem>
                {filterOptions.entity_types.map(et => (
                  <SelectItem key={et} value={et}>{entityLabel(et)}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* User filter (admin only) */}
            {isAdmin && (
              <Select value={selectedUserId} onValueChange={(val) => { setSelectedUserId(val === 'all' ? '' : val); setPage(1); }}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder={t('filters.user')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('filters.allUsers')}</SelectItem>
                  {filterOptions.users.map(u => (
                    <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Date from */}
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="h-9"
              placeholder={t('filters.from')}
            />

            {/* Date to */}
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="h-9"
              placeholder={t('filters.to')}
            />
          </div>
        </CardContent>
      </Card>

      {/* Logs list */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
        </div>
      ) : (
        <div className="space-y-3">
          {logs.map((log) => (
            <Card key={log.log_id} className="hover:shadow-md transition-shadow duration-200" data-testid={`log-${log.log_id}`}>
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg shrink-0 mt-0.5">
                    {log.user_name ? (
                      <User className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                    ) : (
                      <Activity className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="font-semibold text-slate-900 dark:text-slate-100 text-sm">
                        {log.user_name || t('system')}
                      </span>
                      <Badge variant="secondary" className={`text-xs px-2 py-0 capitalize ${getActionColor(log.action)}`}>
                        {actionLabel(log.action)}
                      </Badge>
                      <Badge variant="outline" className={`text-xs px-2 py-0 capitalize ${getEntityBadge(log.entity_type)}`}>
                        {entityLabel(log.entity_type)}
                      </Badge>
                      <span className="text-xs text-slate-400 dark:text-slate-500 ml-auto shrink-0">
                        {formatDate(log.created_at)}
                      </span>
                    </div>
                    {log.details && (
                      <p className="text-sm text-slate-600 dark:text-slate-400 break-words">{log.details}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {logs.length === 0 && (
            <div className="text-center py-12">
              <Activity className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600 dark:text-slate-400">{t('empty')}</p>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('pagination.page', { page, total: totalPages })}
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
              <ChevronLeft className="h-4 w-4 mr-1" /> {t('pagination.previous')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
              {t('pagination.next')} <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ActivityLogs;